import { supabase } from "@/lib/supabaseClient";
import type { PropertyType } from "@/lib/supabase/types";

/**
 * CR (Commercial Registration) lookup.
 *
 * Sprint 0: queries a local mock table seeded with a handful of Oman
 * companies. In production (sprint 4) this becomes a thin wrapper
 * around the real MOCIIP / Invest Easy registry API — the function
 * signature below stays stable so components don't need to change.
 */

export interface CrRegistryHit {
  cr_number: string;
  name_en: string;
  name_ar: string | null;
  sector: string | null;
  suggested_property_type: PropertyType;
  address: string | null;
  phone: string | null;
  owner_name: string | null;
  status: string;
}

/** Look up a single CR number. Returns `null` when no match. */
export async function lookupCr(crNumber: string): Promise<CrRegistryHit | null> {
  const trimmed = crNumber.trim();
  if (!trimmed) return null;

  const { data, error } = await supabase
    .from("cr_registry_mock")
    .select("*")
    .eq("cr_number", trimmed)
    .maybeSingle();

  if (error) {
    // Swallow — the caller shows a "not found" state for anything
    // other than a clean hit. Real registry APIs will throw for
    // auth/network issues, which we'll surface then.
    console.warn("[crRegistry] lookup failed", error);
    return null;
  }
  return (data as CrRegistryHit) ?? null;
}
